<script setup lang="ts">
import type { ArticleSummary } from '@/api/articles/types';
import { LANGUAGE } from '@/env';
import styles from '@/assets/sass/components/article-item.module.scss';
import { formatDateLocal, parseIsoDate } from '@/utilities/dateFormatter';
import DOMPurify from 'dompurify';
import { computed, shallowRef, watch } from 'vue';
import { getEntityFiles } from '@/api/entities/getEntityFiles';
import { getHeroFile } from '@/api/utils/helpers/getHeroFile';
import { getFileUrl } from '@/api/utils/helpers/getFileUrl';

interface Props {
    article: ArticleSummary;
}

interface Image {
    url: string,
    alt: string | null,
}

const props = defineProps<Props>();
const image = shallowRef<Image | null>(null);

const sanitizedExcerpt = computed(() => {
    return DOMPurify.sanitize(props.article.excerpt[LANGUAGE] || '');
})


async function loadImage(id: string) {
    image.value = null;

    const files = await getEntityFiles(id);

    const hero = getHeroFile(files);

    if (hero) {
        image.value = {
            url: getFileUrl(hero.id),
            alt: hero.name[LANGUAGE],
        };
    }

}

watch(() => props.article.id, (newId) => {
    loadImage(newId);
}, { immediate: true });

const articleLink = computed(() => ({
    name: 'articleDetail',
    params: {
        articleSlug: props.article.slug[LANGUAGE]
    }
}))
</script>
<template>
    <article :class="styles['article-item']">
        <RouterLink :class="styles['image-container']" :to="articleLink">
            <img :class="styles.image" :src="image?.url || undefined" :alt="image?.alt || undefined" />
        </RouterLink>
        <div :class="styles.text">
            <h2 :class="styles.heading">
                <RouterLink :class="styles.link" :to="articleLink">
                    {{ props.article.title[LANGUAGE] }}
                </RouterLink>
            </h2>
            <div :class="styles.date">
                {{ formatDateLocal(parseIsoDate(props.article.published_at)) }}
            </div>
            <div :class="styles.summary" v-html="sanitizedExcerpt">
            </div>
        </div>
    </article>
</template>
